
import React from 'react';
import { Logo } from './navigation/Logo';
import { DesktopNavigation } from './navigation/DesktopNavigation';
import { ActionButtons } from './navigation/ActionButtons';
import { MobileMenuButton } from './navigation/MobileMenuButton';
import { MobileNavigation } from './navigation/MobileNavigation';
import { SearchModal } from './navigation/SearchModal';
import { useNavigationHandlers } from './navigation/useNavigationHandlers';

export const Navigation = () => {
  const {
    isOpen,
    setIsOpen,
    mounted,
    theme,
    handleThemeToggle,
    navItems,
    isActive,
    megaMenuOpen,
    searchModalOpen,
    handleMouseEnterNavItem,
    handleMouseLeaveNavItem,
    clearMenuTimeout,
    closeMegaMenuImmediately,
    resetInactivityTimer,
    handleNavigationClick,
    handleSearchOpen,
    handleSearchClose
  } = useNavigationHandlers();

  // Calculate dynamic top position for mega menu overlays
  const navRef = React.useRef<HTMLElement | null>(null);
  const [overlayTop, setOverlayTop] = React.useState(0);

  React.useEffect(() => {
    const updateTop = () => {
      const el = navRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setOverlayTop(rect.top + rect.height);
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    window.addEventListener('scroll', updateTop, { passive: true } as AddEventListenerOptions);
    return () => {
      window.removeEventListener('resize', updateTop);
      window.removeEventListener('scroll', updateTop);
    };
  }, []);

  return (
    <>
      <nav
        ref={navRef}
        className="w-full z-50 border-b border-border bg-background/95 text-foreground backdrop-blur-sm fixed top-0 left-0 right-0 lg:top-11 [content-visibility:auto] [contain-intrinsic-size:1px_80px]"
        aria-label="Main navigation"
        role="navigation"
      >
          <div className="container mx-auto px-3 md:px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
            <Logo />

              <div className="hidden flex-1 items-center justify-center lg:flex">
              <DesktopNavigation
                navItems={navItems}
                isActive={isActive}
                megaMenuOpen={megaMenuOpen}
                onMouseEnterNavItem={handleMouseEnterNavItem}
                onMouseLeaveNavItem={handleMouseLeaveNavItem}
                onMouseEnterMegaMenuContent={clearMenuTimeout} 
                onMouseLeaveMegaMenuContent={handleMouseLeaveNavItem}
                onCloseMegaMenu={closeMegaMenuImmediately}
                onNavigationClick={handleNavigationClick}
                onUserActivity={resetInactivityTimer}
                overlayTop={overlayTop}
              />
              </div>

            <ActionButtons 
              mounted={mounted}
              theme={theme}
              onThemeToggle={handleThemeToggle}
              onSearchOpen={handleSearchOpen}
            />

              <div className="flex lg:hidden">
               <MobileMenuButton
                  mounted={mounted}
                  theme={theme}
                  onThemeToggle={handleThemeToggle}
                  isOpen={isOpen}
                  onToggle={() => setIsOpen(!isOpen)}
               />
              </div>
          </div>
        </div>
      </nav>
      
      {/* Mobile navigation - rendered outside nav for full viewport takeover */}
      <MobileNavigation
        isOpen={isOpen}
        navItems={navItems}
        isActive={isActive}
        onClose={() => setIsOpen(false)}
        onNavigationClick={handleNavigationClick}
      />
      
      {/* Search Modal */}
      <SearchModal
        isOpen={searchModalOpen}
        onClose={handleSearchClose}
      />
      {/* Height adjusted for Navigation, plus the 44px TopMenuBar on lg+. */}
      <div className="h-16 md:h-20 lg:h-[8.75rem]" id="main-content" tabIndex={-1}></div>
    </>
  );
};

// Re-export navigation components for convenience
export { RelatedLinksBlock } from './navigation/RelatedLinksBlock';
export { RouteProgressIndicator } from './navigation/RouteProgressIndicator';
export { SmartScrollRestoration } from './navigation/SmartScrollRestoration';
